'use client';

import React, { createContext, forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

function toArray(activeKey) {
  if (!Array.isArray(activeKey)) {
    return activeKey ? [activeKey] : [];
  }
  return activeKey;
}

export interface IProps {
  /**
   * The child elements to be rendered inside the panel group
   */
  children: React.ReactNode,
  /**
   * Optional prefix for the CSS class
   */
  prefixCls?: string,
  /**
   * Key(s) of the currently active panel(s)
   */
  activeKey?: string | string[],
  /**
   * Key(s) of the initially active panel(s)
   */
  defaultActiveKey?: string | string[],
  /**
   * Boolean indicating if the panels should function as an accordion
   */
  accordion?: boolean,
  /**
   * Keys of panels that should be expanded by default
   */
  expandBy?: string[],
  /**
   * Callback function triggered when the active panel(s) change
   */
  onChange?: (activeKey: string[]) => void,
  /**
   * Optional CSS class name for custom styling
   */
  className?: string,
  /**
   * Inline styles for the component
   */
  style?: Record<string, string | number | null>,
  /**
   * Unique identifier for testing purposes
   */
  dataTest: string,
}

export const PanelGroupContext = createContext(null);

export function usePanelGroupContext() {
  const context = useContext(PanelGroupContext);
  if (!context) {
    throw new Error('You try to use Panel component without covering PanelGroup, must be covered PanelGroup');
  }
  return context;
}

const PanelGroup = forwardRef((props: IProps, parentRef) => {
  const {
    prefixCls = 'panelGroup',
    className,
    accordion = false,
    style,
    dataTest,
    expandBy = [],
    onChange = () => null,
  } = props;

  const [arrayPanels, setArrayPanels] = useState<string[]>([]);
  const [activeKey, setActiveKey] = useState<string[]>(() => {
    if (expandBy.length > 0) {
      return expandBy;
    }

    return toArray(props.activeKey || props.defaultActiveKey);
  });

  const [defaultActiveKey, setDefaultActiveKey] = useState(props.defaultActiveKey);

  useImperativeHandle(parentRef, () => ({
    getActiveKeys: () => activeKey,
    getItems: () => arrayPanels,
    setActiveKey: (key: string | string[]) => {
      changeActiveKey(toArray(key));
    },
  }));

  const collapseClassName = cn(prefixCls, className);

  useEffect(() => {
    if ('activeKey' in props) {
      setActiveKey(toArray(props.activeKey));
    }

    if (props.defaultActiveKey && !isEqual(defaultActiveKey, props.defaultActiveKey)) {
      setActiveKey(toArray(props.defaultActiveKey));
      setDefaultActiveKey(props.defaultActiveKey);
    }
  }, [props.defaultActiveKey, props.activeKey]);

  const onClickItem = (key: string) => {
    if (accordion) {
      changeActiveKey(activeKey[0] === key ? [] : [key]);
      return;
    }

    const index = activeKey.indexOf(key);
    const newActiveKey = index > -1
      ? activeKey.filter((k) => k !== key)
      : [...activeKey, key];

    changeActiveKey(newActiveKey);
  };

  const changeActiveKey = (newActiveKey: string[]) => {
    if (!('activeKey' in props)) {
      setActiveKey(newActiveKey);
    }
    onChange(newActiveKey);
  };

  const getItemProps = ({ disabled, id }: { disabled: boolean, id: string }): {
    prefixCls: string,
    onItemClick: () => void,
    isActive: boolean,
  } => {
    const isActive = accordion ? activeKey[0] === id : activeKey.includes(id);

    if (!arrayPanels.includes(id)) {
      setArrayPanels((prev) => [...prev, id]);
    }

    return {
      isActive,
      prefixCls,
      onItemClick: disabled ? () => {} : () => onClickItem(id),
    };
  };

  const destroyPanel = (key: string) => {
    setArrayPanels((prev) => prev.filter((item) => item !== key));
  };

  return (
    <PanelGroupContext.Provider
      value={ {
        getItemProps,
        activeKey,
        destroyPanel,
      } }
    >
      <div
        className={ collapseClassName }
        style={ style }
        data-test={ dataTest }
      >
        { props.children }
      </div>
    </PanelGroupContext.Provider>
  );
});

export default PanelGroup;
